/**
 * SQLite file header inspection — runs on every import (T8 of the
 * plan, per the D6 user refinement: "an app can become a writer
 * between attach and import").
 *
 * The check has two responsibilities:
 *
 *  1. Verify the bytes are actually a SQLite database (magic header).
 *     This catches a `.csv`-extension-stripped-and-renamed file from
 *     being silently accepted by the sqlite-wasm engine (its file
 *     loaders are forgiving and only surface the problem at query
 *     time).
 *
 *  2. Detect WAL journal mode. A WAL-mode SQLite database has its
 *     recent writes in a sidecar `*-wal` file that the browser's FSA
 *     picker doesn't surface. Reading the main file alone would
 *     return stale data — every row count, every value, possibly
 *     missing whole rows. Per the D6 decision we refuse import on
 *     ANY WAL-mode database; the user runs
 *     `PRAGMA wal_checkpoint(TRUNCATE)` to fold the WAL back into
 *     the main file, then re-imports.
 *
 * The check happens on every import, not just attach, because the
 * file the user pointed at could have been opened by another app
 * mid-session — e.g. they're connected to a long-running SQLite
 * process that switched the DB to WAL mode between MyCorr's attach
 * and the user's first import click.
 *
 * Magic header reference (SQLite docs §1.3 "The Database Header"):
 *   bytes 0..15  : "SQLite format 3\0"
 *   byte  18     : file format write version (1=legacy, 2=WAL)
 *   byte  19     : file format read version  (1=legacy, 2=WAL)
 *   bytes 24..27 : file change counter (big-endian u32; incremented
 *                  every commit — useful for cache invalidation, not
 *                  used here)
 */

/** Minimum bytes required for a full SQLite header read. The header
 *  itself is exactly 100 bytes; we read 100 to stay aligned. */
export const SQLITE_HEADER_BYTES = 100;

const MAGIC = new Uint8Array([
  0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74,
  0x20, 0x33, 0x00,
]); // "SQLite format 3\0"

export interface SqliteHeaderInfo {
  /** True iff the first 16 bytes match the canonical SQLite magic. */
  isSqliteFile: boolean;
  /** True iff the file format write/read version field at byte 18 or
   *  19 is `2` (WAL mode). Either flag is sufficient — a file can be
   *  read-WAL but not write-WAL (extremely rare; we treat it the same). */
  isWalMode: boolean;
  /** Raw write-version byte. Surfaced for debugging / error messages. */
  fileFormatWriteVersion: number;
  /** Raw read-version byte. Surfaced for debugging / error messages. */
  fileFormatReadVersion: number;
}

/**
 * Parse the SQLite database header from a 100-byte buffer.
 *
 * Returns structurally for any input — a non-SQLite file has
 * `isSqliteFile: false`, and the WAL/version fields are still
 * populated (they're meaningless on a non-SQLite file but the caller
 * may want to log them).
 */
export function parseSqliteHeader(bytes: Uint8Array): SqliteHeaderInfo {
  if (bytes.length < SQLITE_HEADER_BYTES) {
    return {
      isSqliteFile: false,
      isWalMode: false,
      fileFormatWriteVersion: 0,
      fileFormatReadVersion: 0,
    };
  }

  let isSqliteFile = true;
  for (let i = 0; i < MAGIC.length; i++) {
    if (bytes[i] !== MAGIC[i]) {
      isSqliteFile = false;
      break;
    }
  }

  const writeV = bytes[18] ?? 0;
  const readV = bytes[19] ?? 0;

  return {
    isSqliteFile,
    isWalMode: isSqliteFile && (writeV === 2 || readV === 2),
    fileFormatWriteVersion: writeV,
    fileFormatReadVersion: readV,
  };
}

/**
 * Standardized error used by both the worker's import handler and the
 * unit tests. The message text is load-bearing: the connector card's
 * import-failure path branches on the substring `WAL` to suggest the
 * PRAGMA fix.
 */
export const WAL_CHECKPOINT_ERROR =
  "This SQLite file is in WAL journal mode with pending writes that the browser " +
  "can't see (the -wal sidecar file isn't accessible via the file picker). " +
  "Open the file in another tool and run `PRAGMA wal_checkpoint(TRUNCATE)`, " +
  "or close the app that has it open, then re-attempt the import.";

export const NOT_SQLITE_ERROR =
  "This file isn't a SQLite database (the magic header `SQLite format 3\\0` " +
  "is missing). If it's a CSV or Parquet file, rename the extension and upload " +
  "via the import dropzone instead.";

/**
 * Read the first 100 bytes of a `FileSystemFileHandle`'s current file
 * and assert it's safe to import — i.e. it's a SQLite database AND
 * is not in WAL mode with pending writes.
 *
 * Called by `SqliteImportTable` on every import (T8) so an external
 * write between attach and import doesn't slip through. The cost is
 * one 100-byte filesystem read; sub-millisecond on any device.
 *
 * Throws with `WAL_CHECKPOINT_ERROR` or `NOT_SQLITE_ERROR` as
 * appropriate; the worker surfaces the message verbatim to the UI.
 */
export async function assertSafeToImport(
  handle: FileSystemFileHandle,
): Promise<void> {
  let file: File;
  try {
    file = await handle.getFile();
  } catch (e) {
    // FSA permission has decayed since attach (handle was structured-
    // cloned through IDB; `queryPermission()` now returns `prompt` or
    // `denied`). Skip the re-check: the OPFS-cached snapshot was
    // verified non-WAL at attach time (this same function blocked any
    // WAL-mode file from ever reaching OPFS), so importing from the
    // cache is safe. The user's `.db` file MAY have been switched to
    // WAL mode since attach, which would mean our snapshot is stale —
    // but that's a freshness concern, surfaced separately by the v1.2.1
    // freshness sweep on the Connections page, not a correctness
    // concern at the row level. Better to silently import the
    // attach-time snapshot than to fail the user's flow over a
    // permission they granted minutes ago.
    if (
      e !== null &&
      typeof e === "object" &&
      ((e as { name?: unknown }).name === "NotAllowedError" ||
        (typeof (e as { message?: unknown }).message === "string" &&
          /not allowed by the user agent|getFile|FileSystemFileHandle/i.test(
            (e as { message: string }).message,
          )))
    ) {
      return;
    }
    throw e;
  }
  const headerBlob = file.slice(0, SQLITE_HEADER_BYTES);
  const buf = new Uint8Array(await headerBlob.arrayBuffer());
  const info = parseSqliteHeader(buf);
  if (!info.isSqliteFile) {
    throw new Error(NOT_SQLITE_ERROR);
  }
  if (info.isWalMode) {
    throw new Error(WAL_CHECKPOINT_ERROR);
  }
}
